import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import FIELDS from '../../../civitfun-police-report-utils/PoliceReportFields';
import PoliceReportConstants from '../../../civitfun-police-report-utils/police.constants';
import { createReportToApiUpdate } from '../../../civitfun-police-report-utils/PoliceReportParse.utils';
import PoliceReportValidations from '../../../civitfun-police-report-utils/validations/AlloggiatioReportValidations';

const {
  ITA_MUNICIPALITIES,
  ITALIAN_ROLE_TYPE,
  ITA_DOCUMENT_TYPE,
  ITA_COUNTRIES,
  ADULT_AGE,
  ITA_GENDERS,
  ITA_PROVINCES,
} = PoliceReportConstants;

const { isMandatoryFieldEmpty, getFieldValidations } = PoliceReportValidations;

export default function usePoliceReportDetailHook(initialReport) {
  const {
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const navigate = useNavigate();

  const { API } = useContext(ApiContext);

  const [state, setState] = useState({
    bookingId: initialReport?.bookingUUID,
    report: {},
    showSendWarning: false,
    disableForm: false,
    isLoading: false,
    errorFields: {
      contract: [],
      guests: Array.from({ length: initialReport?.numGuests }, () => []),
    },
    checkinCount: initialReport[FIELDS.NUMGUESTS_KEY],
    showBookingDeleteWarning: false,
    showGuestDeleteWarning: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    actions.getPoliceReportDetail();

    if (initialReport.status !== 'error') {
      updateState({ disableForm: true });
    }
  }, []);

  const actions = {
    createOptionsFromType: (typeArray, translationPrefix) =>
      typeArray.map(type => ({
        value: translationPrefix ? type : type.value,
        label: translationPrefix
          ? translate(`policeReport.${translationPrefix}.${type}`)
          : type.label,
      })),

    getRoleTypeOptions: () => actions.createOptionsFromType(ITALIAN_ROLE_TYPE, 'itaRole'),
    getDocumentTypeOptions: () =>
      actions.createOptionsFromType(ITA_DOCUMENT_TYPE, 'itaDocumentType'),
    getMunicipalityOptions: () => actions.createOptionsFromType(ITA_MUNICIPALITIES),
    getCountryOptions: () => actions.createOptionsFromType(ITA_COUNTRIES),
    getGenderOptions: () => actions.createOptionsFromType(ITA_GENDERS, 'gender'),
    onSendClicked: () => {
      actions.setShowSendWarning(true);
    },

    validateForm: ({ showErrorMsg, report }) => {
      const errors = {
        contract: [],
        guests: Array.from({ length: report.guests.length }, () => []),
      };

      const fieldValidations = getFieldValidations(report, translate);

      // ---------------------------
      // Validaciones generales
      // ---------------------------
      for (const field of Object.values(FIELDS)) {
        // Contrato
        if (
          [
            FIELDS.REFERENCE_KEY,
            FIELDS.CONTRACTDATE_KEY,
            FIELDS.ENTRANCE_KEY,
            FIELDS.DEPARTURE_KEY,
            FIELDS.NUMGUESTS_KEY,
            FIELDS.PAXES_KEY,
            FIELDS.ADULTS_KEY,
            FIELDS.CHILDREN_KEY,
          ].includes(field)
        ) {
          if (isMandatoryFieldEmpty(report.contract, field)) errors.contract.push(field);
        } else {
          // Huéspedes
          for (let i = 0; i < report.guests.length; i++) {
            const guest = report.guests[i];
            if (isMandatoryFieldEmpty(guest, field)) errors.guests[i].push(field);
          }
        }

        // Validaciones extra
        if (field in fieldValidations) {
          if (
            [
              FIELDS.REFERENCE_KEY,
              FIELDS.CONTRACTDATE_KEY,
              FIELDS.ENTRANCE_KEY,
              FIELDS.DEPARTURE_KEY,
              FIELDS.NUMGUESTS_KEY,
              FIELDS.PAXES_KEY,
              FIELDS.ADULTS_KEY,
              FIELDS.CHILDREN_KEY,
            ].includes(field)
          ) {
            if (!fieldValidations[field].validate()) {
              if (!errors.contract.includes(field)) errors.contract.push(field);
            }
          } else {
            for (let i = 0; i < report.guests.length; i++) {
              const guest = report.guests[i];
              if (!fieldValidations[field].validate(guest)) {
                if (!errors.guests[i].includes(field)) errors.guests[i].push(field);
              }
            }
          }
        }
      }

      // ---------------------------
      // Reglas documento (todos los huéspedes)
      // ---------------------------
      for (let i = 0; i < report.guests.length; i++) {
        const g = report.guests[i];
        const val = v => (v && typeof v === 'object' && 'value' in v ? v.value : v);

        const pushIfEmpty = f => {
          if (!val(g?.[f])) errors.guests[i].push(f);
        };

        // Obligatorios para todos
        pushIfEmpty(FIELDS.DOCUMENTTYPE_KEY);
        pushIfEmpty(FIELDS.DOCUMENTNUMBER_KEY);
        pushIfEmpty(FIELDS.DOCUMENTISSUANCECOUNTRY_KEY);

        // Si emisión es Italia
        if (val(g?.[FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]) === 'ITA') {
          pushIfEmpty(FIELDS.DOCUMENTISSUANCEMUNICIPALITY_KEY);

          // Provincia
          const muni = val(g?.[FIELDS.DOCUMENTISSUANCEMUNICIPALITY_KEY]) || '';
          const prov = val(g?.[FIELDS.DOCUMENTISSUANCEPROVINCE_KEY]);
          let ok = false;

          if (prov && prov.length === 2 && ITA_PROVINCES?.[prov]) ok = true;
          else if (/^\d{9}$/.test(muni)) {
            const cityObj = ITA_MUNICIPALITIES.find(m => m.value === muni);
            const code = cityObj?.meta?.province;
            if (code && ITA_PROVINCES?.[code]) ok = true;
          }

          if (!ok) errors.guests[i].push(FIELDS.DOCUMENTISSUANCEPROVINCE_KEY);
        }
      }

      // ---------------------------
      // Final
      // ---------------------------
      const hasErrors = errors.contract.length > 0 || errors.guests.some(arr => arr.length > 0);

      if (showErrorMsg && hasErrors) {
        notification.error({
          message: translate('policeReport.mandatoryFieldsError'),
        });
      }

      updateState({ errorFields: errors, showSendWarning: false });
      return !hasErrors;
    },

    getPoliceReportDetail: () => {
      const mock = pickMockByReference(initialReport);

      actions.validateForm({ showErrorMsg: false, report: mock });
      updateState({ isLoading: false, report: mock });

      return;

      // TODO: remove above lines when API is ready
      API.getPoliceReportDetail({
        hotelId: getSelectedHotel().key || getSelectedHotel().id,
        bookingId: state.bookingId,
      })
        .then(res => {
          if (initialReport.status == 'error') {
            // actions.validateForm({ showErrorMsg: false, report: res });
          }
          updateState({ isLoading: false, report: res });
        })
        .catch(err => {
          console.error(err);
          updateState({ isLoading: false });
          notification.error({
            message: translate('policeReport.edit.error'),
          });
        });
    },

    sendReport: () => {
      if (!actions.validateForm({ showErrorMsg: true, report: state.report })) return;
      notification.success({
        message: translate('policeReport.edit.success'),
      });
      return; // TODO: remove when API is ready
      updateState({ isLoading: false });

      updateState({ isLoading: true, disableForm: true, showSendWarning: false });

      API.postSendAlloggiatiPoliceReport({
        hotelId: getSelectedHotel().key || getSelectedHotel().id,
        data: createReportToApiUpdate(
          state.report,
          getSelectedHotel().key || getSelectedHotel().id
        ),
      })
        .then(_ => {
          notification.success({
            message: translate('policeReport.edit.success'),
          });
          updateState({ isLoading: false, disableForm: true });
        })
        .catch(_ => {
          notification.error({
            message: translate('policeReport.edit.error'),
          });
          updateState({ isLoading: false, disableForm: false });
        });
    },

    setShowSendWarning: show => {
      updateState({ showSendWarning: show });
    },

    closeSendWarning: () => {
      updateState({ showSendWarning: false });
    },

    confirmSend: () => {
      actions.sendReport();
    },

    updateGuestField: (guestIndex, key, value) => {
      const report = JSON.parse(JSON.stringify(state.report));
      const guest = report.guests[guestIndex];

      guest[key] = value;

      if (key === FIELDS.BIRTHCITY_KEY) {
        const provObj = actions.getProvince(value); // value = 420095001

        guest[FIELDS.BIRTHPROVINCE_KEY] = provObj?.code || '';
      }

      updateState({ report });
    },

    updateContractField: (key, value) => {
      const report = JSON.parse(JSON.stringify(state.report));
      report.contract[key] = value;
      updateState({ report });
    },

    onBackClicked: () => {
      navigate(-1);
    },
    getProvince: value => {
      if (!value) return null;

      const provs = ITA_PROVINCES; // { PD: "Padova", ... }

      // 1️⃣ Si value es un código de provincia (2 letras)
      if (typeof value === 'string' && value.length === 2 && provs[value]) {
        return { code: value, label: provs[value] };
      }

      // 2️⃣ Si value es un código ISTAT: buscar municipio por value
      const municipalityByValue = ITA_MUNICIPALITIES.find(m => m.value === value);

      if (municipalityByValue?.meta?.province) {
        const code = municipalityByValue.meta.province;
        const name = provs[code];
        return name ? { code, label: name } : null;
      }

      // 3️⃣ Si value es el nombre del municipio (solo fallback)
      const municipalityByLabel = ITA_MUNICIPALITIES.find(
        m => m.label.toLowerCase() === value.toLowerCase()
      );

      if (municipalityByLabel?.meta?.province) {
        const code = municipalityByLabel.meta.province;
        const name = provs[code];
        return name ? { code, label: name } : null;
      }

      return null;
    },
    onDeleteBookingClicked: () => {
      actions.setShowBookingDeleteWarning(true);
    },
    onDeleteGuestClicked: guest => {
      updateState({ guestToDelete: guest });

      actions.setShowGuestDeleteWarning(true);
    },
    setShowBookingDeleteWarning: show => {
      updateState({ showBookingDeleteWarning: show });
    },
    setShowGuestDeleteWarning: show => {
      updateState({ showGuestDeleteWarning: show });
    },
    sendDeleteBooking: () => {
      notification.success({
        message: translate('policeReport.delete.success'),
      });
      return;
      updateState({ isLoading: true });
      const bookingId = state.bookingId;
      const date = state.report.contract?.entrance;

      const data = {
        bookingId, // uuid
        date,
        hotelId: getSelectedHotel().key,
      };
      API.policeReportSendDelete(data)
        .then(res => {
          notification.success({
            message: translate('policeReport.delete.success'),
          });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
          navigate(-1);
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.delete.error'),
          });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
        });
    },
    sendDeleteGuest: () => {
      notification.success({
        message: translate('policeReport.delete.success'),
      });
      return;
      updateState({ isLoading: true });

      const guestId = state.guestToDelete.id;
      const date = state.report.contract?.entrance;

      const data = {
        guestId,
        date,
        hotelId: getSelectedHotel().key,
      };

      API.policeReportSendDelete(data)
        .then(res => {
          notification.success({
            message: translate('policeReport.delete.success'),
          });

          // Delete the guest for front-End
          const updatedGuests = state.report.guests.filter(guest => guest.id !== guestId);
          const updatedReport = { ...state.report, guests: updatedGuests };

          updateState({
            isLoading: false,
            showGuestDeleteWarning: false,
            report: updatedReport,
          });

          window.location.reload(); // reloag page
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.delete.error'),
          });
          updateState({ isLoading: false, showGuestDeleteWarning: false });
        });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}

// === Mocks de detalle (3 reservas) ===

const mockedReport_IT_OK = {
  contract: {
    reference: '504767515',
    contractDate: '2026-01-25',
    entrance: '2026-01-27',
    departure: '2026-01-30',
    paxes: 1,
    numGuests: 1,
    adults: 1,
    children: 0,
  },
  guests: [
    {
      // Huésped titular OK (Italia)
      [FIELDS.ROLETYPE_KEY]: 'OSPITE SINGOLO',
      name: 'Luca',
      surname: 'Bianchi',
      secondSurname: '',
      country: 'ITA', // residencia/país en UI (si lo usas)
      gender: 'M',
      birthCountry: 'ITA', // ⇦ nacimiento en Italia
      nationality: 'ITA',
      birthDate: '1985-06-10',
      birthCity: '420095001', // ⇦ ISTAT (Abano Terme)
      birthProvince: 'PD', // ⇦ sigla 2 letras
      // Documento titular (completo)
      [FIELDS.DOCUMENTTYPE_KEY]: 'IDENT',
      [FIELDS.DOCUMENTNUMBER_KEY]: 'YA1234567',
      [FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]: 'ESP', // ⇦ uso NO ITA para evitar exigir comune/provincia de expedición
    },
  ],
};

const mockedReport_ES_ERR = {
  contract: {
    reference: '505155181',
    contractDate: '2026-01-20',
    entrance: '2026-01-27',
    departure: '2026-01-28',
    paxes: 1,
    numGuests: 1,
    adults: 1,
    children: 0,
  },
  guests: [
    {
      // Huésped titular con errores de documento (para provocar validación)
      [FIELDS.ROLETYPE_KEY]: 'OSPITE SINGOLO',
      name: 'Sofía',
      surname: 'Martínez',
      secondSurname: 'López',
      country: 'ESP',
      gender: 'F',
      birthCountry: 'ESP', // ⇦ nacimiento fuera de Italia (no obliga comune/provincia)
      nationality: 'ESP',
      birthDate: '1990-03-22',
      birthCity: '',
      birthProvince: '',
      // Documento INCOMPLETO (provoca error de validación del titular)
      [FIELDS.DOCUMENTTYPE_KEY]: '',
      [FIELDS.DOCUMENTNUMBER_KEY]: '',
      [FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]: '',
    },
  ],
};

const mockedReport_US_2G_ERR = {
  contract: {
    reference: '507403672',
    contractDate: '2026-01-18',
    entrance: '2026-01-27',
    departure: '2026-01-29',
    paxes: 2,
    numGuests: 2,
    adults: 2,
    children: 0,
  },
  guests: [
    {
      // Huésped 1 (titular) con error en documento
      [FIELDS.ROLETYPE_KEY]: 'CAPO GRUPPO',
      name: 'Emma',
      surname: 'Johnson',
      secondSurname: '',
      country: 'USA',
      gender: 'F',
      birthCountry: 'USA',
      nationality: 'USA',
      birthDate: '1988-11-05',
      birthCity: '',
      birthProvince: '',
      // Documento INCOMPLETO
      [FIELDS.DOCUMENTTYPE_KEY]: '',
      [FIELDS.DOCUMENTNUMBER_KEY]: '',
      [FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]: '',
    },
    {
      // Huésped 2 (acompañante) con otro error simple
      [FIELDS.ROLETYPE_KEY]: 'MEMBRO GRUPPO',
      name: 'Olivia',
      surname: 'Williams',
      secondSurname: '',
      country: 'USA',
      gender: 'F',
      birthCountry: 'ITA',
      nationality: 'USA',
      birthDate: '1991-02-14',
      birthCity: '',
      birthProvince: '',
      // También sin documento
      [FIELDS.DOCUMENTTYPE_KEY]: '',
      [FIELDS.DOCUMENTNUMBER_KEY]: '',
      [FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]: '',
    },
  ],
};

// Helper para seleccionar el mock por reference
function pickMockByReference(initialReport) {
  const ref =
    initialReport?.[FIELDS.REFERENCE_KEY] ||
    initialReport?.reference ||
    initialReport?.contract?.reference;

  switch (ref) {
    case '504767515':
      return mockedReport_IT_OK;
    case '505155181':
      return mockedReport_ES_ERR;
    case '507403672':
      return mockedReport_US_2G_ERR;
    default:
      // fallback útil durante pruebas
      return mockedReport_IT_OK;
  }
}
